import { useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Button, Stack } from "@mui/material"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import BackIcon from "../../components/BackIcon"
import dayjs from "dayjs"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomAutocomplete from "../../components/CustomAutocomplete"
import CustomDatePicker from "../../components/CustomDatePicker"
import CustomFieldset from "../../components/CustomFieldset"
import CustomFieldsetAccordion from "../../components/CustomFieldsetAccordion"
import CustomAggregationSegregationComponent from "../../components/CustomAggregationSegregationComponent"
import { useQuery } from "@tanstack/react-query"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const ContainerFiltrosGenerales = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto auto auto auto",
  gridTemplateAreas: `
          "ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad ILocalidad"
          "IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario IUsuario"
          "IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha IRangoFecha"
          "IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar IBtnFiltrar"
        `,
  gap: "8px",
  rowGap: "12px",
  alignItems: "center",
}))

const ILocalidad = styled(Box)({
  gridArea: "ILocalidad",
})

const IUsuario = styled(Box)({
  gridArea: "IUsuario",
})

const IRangoFecha = styled(Box)({
  gridArea: "IRangoFecha",
})

const IBtnFiltrar = styled(Box)({
  gridArea: "IBtnFiltrar",
})

const ContainerRangoFechaInner = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto",
  gridTemplateAreas: `
            "IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal"
          `,
  gap: "8px",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto",
    gridTemplateAreas: `
      "IFechaInicial"
      "IFechaFinal"
    `,
    gap: "12px",
  },
}))

const RangoFechaIFechaInicial = styled(Box)({
  gridArea: "IFechaInicial",
})

const RangoFechaIFechaFinal = styled(Box)({
  gridArea: "IFechaFinal",
})

// Hook para obtener localidades
function useGetAllLocalidades() {
  return useQuery({
    queryKey: ["TiemposEjecucionResumenLocalidades"],
    queryFn: async () => {
      const response = await fetchwrapper(`/ConsultaDeCedulaEventos/getLocalidades`)
      const result = await response.json()
      return result.data || []
    },
    onError: () => {
      console.log("Error fetching data localidades")
    },
  })
}

// Hook para obtener usuarios
function useGetAllUsuarios() {
  return useQuery({
    queryKey: ["TiemposEjecucionResumenUsuarios"],
    queryFn: async () => {
      const response = await fetchwrapper(`/ConsultaDeCedulaEventos/getUsuarios`)
      const result = await response.json()
      return result.data || []
    },
    onError: () => {
      console.log("Error fetching data usuarios")
    },
  })
}

// Hook para obtener datos principales
function useGetTiemposEjecucionData(filtros) {
  return useQuery({
    queryKey: ["TiemposEjecucionResumenData", filtros],
    queryFn: async () => {
      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          externalFilters: {
            rangoFecha: [filtros.fechaInicio, filtros.fechaFin],
            loccodigo: filtros.loccodigo,
            usrcodigo: filtros.usrcodigo,
            eventocodigo: filtros.eventocodigo,
          },
        }),
      }
      const response = await fetchwrapper(`/TiemposEjecucionTareaResumen/getAllInfo`, options)
      const result = await response.json()
      return result.data || []
    },
    enabled: false, // Se ejecutará manualmente al hacer clic en Filtrar
  })
}

// Función para determinar colores basados en el estado de cumplimiento
const determinarColorEstadoCumplimiento = (estado) => {
  const estadoUpper = estado?.toUpperCase() || ""

  if (estadoUpper === "A TIEMPO") {
    return { bg: "#4caf50", text: "#ffffff" } // Verde
  } else if (estadoUpper === "ATRASADO") {
    return { bg: "#f44336", text: "#ffffff" } // Rojo
  }

  return { bg: "#e0e0e0", text: "#000000" } // Default gris
}

// Función para determinar color del balance de minutos
const determinarColorBalance = (balance) => {
  if (balance >= 0) {
    return "#4caf50" // Verde para ahorro
  } else {
    return "#f44336" // Rojo para retraso
  }
}

const TiemposEjecucionTareaResumen = () => {
  const [expanded, setExpanded] = useState(true)
  const [filtros, setFiltros] = useState({
    localidadCB: null,
    loccodigo: "",
    locdescri: "",
    usuarioCB: null,
    usrcodigo: "",
    usrnombre: "",
    eventocodigo: "",
    fechaInicio: dayjs().subtract(30, "day"),
    fechaFin: dayjs(),
  })

  const handleSetFiltros = (k, v) => setFiltros((prev) => ({ ...prev, [k]: v }))

  const {
    data: dataLocalidades = [],
    isLoading: isLoadingLocalidades,
    isFetching: isFetchingLocalidades,
  } = useGetAllLocalidades()

  const { data: dataUsuarios = [], isLoading: isLoadingUsuarios, isFetching: isFetchingUsuarios } = useGetAllUsuarios()

  const {
    data: dataTiemposEjecucion = [],
    isLoading: isLoadingData,
    isFetching: isFetchingData,
    refetch: refetchData,
  } = useGetTiemposEjecucionData(filtros)

  const handleToggle = () => {
    setExpanded(!expanded)
  }

  const handleClickFiltrarBtn = async () => {
    await refetchData()
  }

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return ""
    return dayjs(dateString).format("DD/MM/YYYY")
  }

  // Función para formatear minutos
  const formatMinutos = (minutos) => {
    if (minutos === null || minutos === undefined) return "0.00"
    return Number(minutos).toFixed(2)
  }

  // Función para formatear el balance con signo
  const formatBalance = (balance) => {
    if (balance === null || balance === undefined) return "0.00"
    const valor = Number(balance).toFixed(2)
    return balance >= 0 ? `+${valor}` : valor
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Tiempos de Ejecución por Tarea (Resumen)</b>
        </div>
        <CustomBackdrop
          isLoading={
            isLoadingLocalidades ||
            isFetchingLocalidades ||
            isLoadingUsuarios ||
            isFetchingUsuarios ||
            isLoadingData ||
            isFetchingData
          }
        />
        <Box className={StyledRoot}>
          <CustomFieldsetAccordion title="Filtros Generales" expanded={expanded} onToggle={handleToggle}>
            <ContainerFiltrosGenerales>
              <ILocalidad>
                <CustomAutocomplete
                  label="Localidad"
                  selectedOption={filtros.localidadCB}
                  setSelectedOption={(v) => {
                    setFiltros((prev) => ({
                      ...prev,
                      localidadCB: v,
                      loccodigo: v?.loccodigo ?? "",
                      locdescri: v?.locdescri ?? "",
                    }))
                  }}
                  options={dataLocalidades}
                  getOptionLabel={(option) => `${option.loccodigo} - ${option.locdescri}`}
                />
              </ILocalidad>

              <IUsuario>
                <CustomAutocomplete
                  label="Usuario"
                  selectedOption={filtros.usuarioCB}
                  setSelectedOption={(v) => {
                    setFiltros((prev) => ({
                      ...prev,
                      usuarioCB: v,
                      usrcodigo: v?.usrcodigo ?? "",
                      usrnombre: v?.usrnombre ?? "",
                    }))
                  }}
                  options={dataUsuarios}
                  getOptionLabel={(option) => `${option.usrcodigo} - ${option.usrnombre}`}
                />
              </IUsuario>

              <IRangoFecha>
                <CustomFieldset title={"Rango de Fechas"} sx={{ marginTop: "15px" }}>
                  <ContainerRangoFechaInner>
                    <RangoFechaIFechaInicial>
                      <CustomDatePicker
                        label="Fecha Inicial"
                        value={filtros.fechaInicio}
                        setValue={(v) => handleSetFiltros("fechaInicio", v)}
                        isOptional={true}
                      />
                    </RangoFechaIFechaInicial>
                    <RangoFechaIFechaFinal>
                      <CustomDatePicker
                        label="Fecha Final"
                        value={filtros.fechaFin}
                        setValue={(v) => handleSetFiltros("fechaFin", v)}
                        isOptional={true}
                      />
                    </RangoFechaIFechaFinal>
                  </ContainerRangoFechaInner>
                </CustomFieldset>
              </IRangoFecha>

              <IBtnFiltrar>
                <Button variant="contained" onClick={handleClickFiltrarBtn}>
                  Filtrar
                </Button>
              </IBtnFiltrar>
            </ContainerFiltrosGenerales>
          </CustomFieldsetAccordion>

          {dataTiemposEjecucion.length > 0 && (
            <CustomAggregationSegregationComponent
              data={dataTiemposEjecucion}
              columnsConfig={({ totalSumMinutosPlanificados, totalSumMinutosEjecutados, totalSumBalanceMinutos }) => [
                {
                  accessorKey: "FechaEvento",
                  header: "Fecha Evento",
                  size: 200,
                  Cell: ({ cell }) => <span>{formatDate(cell.getValue())}</span>,
                },
                {
                  accessorKey: "AnioEvento",
                  header: "Año",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "MesEvento",
                  header: "Mes",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "locdescri",
                  header: "Localidad",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "usrnombre",
                  header: "Usuario",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "eventocodigo",
                  header: "Evento",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "procesocod",
                  header: "Proceso",
                  size: 200,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "pregdescri",
                  header: "Actividad",
                  size: 250,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "clinombre",
                  header: "Cliente",
                  size: 250,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "MinutosPlanificados",
                  header: "Min. Planificados",
                  size: 250,
                  accessorFn: (row) => Number(row.MinutosPlanificados) || 0,
                  aggregationFn: "sum",
                  AggregatedCell: ({ cell, table }) => (
                    <>
                      Suma por {table.getColumn(cell.row.groupingColumnId ?? "").columnDef.header}:{" "}
                      <Box sx={{ color: "primary.main", fontWeight: "bold" }}>{cell.getValue()?.toFixed(2)} min</Box>
                    </>
                  ),
                  Cell: ({ cell }) => <span>{formatMinutos(cell.getValue())} min</span>,
                  Footer: () => (
                    <Stack>
                      Total Planificado:
                      <Box color="primary.main" fontWeight="bold">
                        {totalSumMinutosPlanificados?.toFixed(2)} min
                      </Box>
                    </Stack>
                  ),
                },
                {
                  accessorKey: "MinutosEjecutados",
                  header: "Min. Ejecutados",
                  size: 250,
                  accessorFn: (row) => Number(row.MinutosEjecutados) || 0,
                  aggregationFn: "sum",
                  AggregatedCell: ({ cell, table }) => (
                    <>
                      Suma por {table.getColumn(cell.row.groupingColumnId ?? "").columnDef.header}:{" "}
                      <Box sx={{ color: "success.main", fontWeight: "bold" }}>{cell.getValue()?.toFixed(2)} min</Box>
                    </>
                  ),
                  Cell: ({ cell }) => <span>{formatMinutos(cell.getValue())} min</span>,
                  Footer: () => (
                    <Stack>
                      Total Ejecutado:
                      <Box color="success.main" fontWeight="bold">
                        {totalSumMinutosEjecutados?.toFixed(2)} min
                      </Box>
                    </Stack>
                  ),
                },
                {
                  accessorKey: "BalanceMinutos",
                  header: "Balance",
                  size: 200,
                  accessorFn: (row) => Number(row.BalanceMinutos) || 0,
                  aggregationFn: "sum",
                  AggregatedCell: ({ cell, table }) => {
                    const valor = cell.getValue()
                    return (
                      <>
                        Balance por {table.getColumn(cell.row.groupingColumnId ?? "").columnDef.header}:{" "}
                        <Box
                          sx={{
                            color: valor >= 0 ? "success.main" : "error.main",
                            fontWeight: "bold",
                          }}
                        >
                          {valor >= 0 ? "+" : ""}
                          {valor?.toFixed(2)} min
                        </Box>
                      </>
                    )
                  },
                  Cell: ({ cell }) => {
                    const balance = cell.getValue()
                    return (
                      <span
                        style={{
                          color: determinarColorBalance(balance),
                          fontWeight: "bold",
                        }}
                      >
                        {formatBalance(balance)} min
                      </span>
                    )
                  },
                  Footer: () => {
                    const balanceTotal = totalSumBalanceMinutos || 0
                    return (
                      <Stack>
                        Balance Total:
                        <Box color={balanceTotal >= 0 ? "success.main" : "error.main"} fontWeight="bold">
                          {balanceTotal >= 0 ? "+" : ""}
                          {balanceTotal?.toFixed(2)} min
                        </Box>
                      </Stack>
                    )
                  },
                },
                {
                  accessorKey: "EstadoCumplimiento",
                  header: "Estado",
                  size: 200,
                  Cell: ({ cell }) => {
                    const estado = cell.getValue()
                    const colores = determinarColorEstadoCumplimiento(estado)

                    return (
                      <span
                        style={{
                          backgroundColor: colores.bg,
                          color: colores.text,
                          fontWeight: "bold",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          display: "inline-block",
                          fontSize: "12px",
                          textAlign: "center",
                          minWidth: "100px",
                        }}
                      >
                        {estado}
                      </span>
                    )
                  },
                },
              ]}
              aggregations={{
                totalSumMinutosPlanificados: (data) =>
                  data.reduce((acc, curr) => acc + Number(curr.MinutosPlanificados || 0), 0),
                totalSumMinutosEjecutados: (data) =>
                  data.reduce((acc, curr) => acc + Number(curr.MinutosEjecutados || 0), 0),
                totalSumBalanceMinutos: (data) => data.reduce((acc, curr) => acc + Number(curr.BalanceMinutos || 0), 0),
              }}
              tableOptions={{
                initialState: {
                  sorting: [{ id: "FechaEvento", desc: true }],
                },
              }}
            />
          )}

          {dataTiemposEjecucion.length === 0 && !isLoadingData && !isFetchingData && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
                border: "1px dashed #ccc",
                borderRadius: "8px",
                marginTop: "20px",
              }}
            >
              <span style={{ color: "#666", fontSize: "16px" }}>
                No hay datos para mostrar. Aplica filtros y haz clic en "Filtrar".
              </span>
            </Box>
          )}
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default TiemposEjecucionTareaResumen
